/**
 * Vendor Portal Layout — wraps all /partner/dashboard/* pages.
 * Separate from staff Layout. Requires vendor auth.
 */
import React from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import {
  Building2, LayoutDashboard, User, FileText, CreditCard, BarChart3,
  Gavel, LogOut, Menu, X,
} from 'lucide-react';
import { useState } from 'react';

const NAV = [
  { to: '/partner/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/partner/dashboard/profile', icon: User, label: 'My Profile' },
  { to: '/partner/dashboard/rfqs', icon: FileText, label: 'My RFQs' },
  { to: '/partner/dashboard/invoices', icon: CreditCard, label: 'Invoices' },
  { to: '/partner/dashboard/ledger', icon: BarChart3, label: 'Ledger' },
  { to: '/partner/dashboard/performance', icon: BarChart3, label: 'Performance' },
  { to: '/partner/dashboard/auctions', icon: Gavel, label: 'Auctions' },
];

export default function VendorPortalLayout() {
  const { vendorUser, logout } = useVendorAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!vendorUser) {
    window.location.href = '/partner/login';
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-60 bg-[#3b4872] flex flex-col transform transition-transform
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:static`}>
        {/* Logo */}
        <div className="px-4 py-4 border-b border-white/10">
          <img
            src="/ellora-logo-blue.png"
            alt="Ellora EPC"
            className="h-8 w-auto"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
          <div className="text-white/50 text-[10px] mt-1 truncate">Partner Portal · {vendorUser.company_name}</div>
        </div>
        {/* Nav */}
        <nav className="flex-1 py-3 space-y-0.5 px-2 overflow-y-auto">
          {NAV.map((n) => {
            const active = location.pathname === n.to || (n.to !== '/partner/dashboard' && location.pathname.startsWith(n.to));
            return (
              <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors
                  ${active ? 'bg-white/15 text-white font-semibold' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}>
                <n.icon size={15} className="shrink-0" />
                {n.label}
              </Link>
            );
          })}
        </nav>
        {/* User + logout */}
        <div className="px-3 py-3 border-t border-white/10">
          <div className="text-white/60 text-[11px] mb-2 truncate">{vendorUser.name} · {vendorUser.email}</div>
          <button onClick={logout}
            className="w-full flex items-center gap-2 text-white/70 hover:text-white text-xs py-1.5 px-2 rounded-sm hover:bg-white/10 transition-colors">
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </aside>
      {/* Overlay */}
      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/50 md:hidden" onClick={() => setMobileOpen(false)} />}
      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile topbar */}
        <header className="md:hidden sticky top-0 z-20 bg-[#3b4872] px-4 h-14 flex items-center justify-between">
          <div className="text-white font-bold text-sm">Partner Portal</div>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="text-white">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </header>
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
